import React, { PureComponent } from 'react';
import moment from 'moment';
import { withTranslation } from 'react-i18next';

import BodyPortal from '../portals/body-portal';

import LocalizedDatePicker from 'components/LocalizedDatePicker';

import Popout from './popout';

const QuickOption = ({ text, date, onSelect }) => {
    const handleClick = () => {
        onSelect(date);
    }

    return (
        <div className="option" onClick={handleClick}>
            { text }
        </div>
    )
}

class CustomDatePicker extends PureComponent {
    static defaultProps = {
        confirmClassName: 'btn-success',
        cancelClassName: 'btn-secondary',
        closeFromDoc: true,
        closeOnClickOut: true
    };

    state = {
        selected: this.props.selected
    }

    handleChange = selected => {
        this.setState({
            selected: moment(selected).format('HH:mm') === '00:00' ? moment(selected).endOf('day').toDate() : selected
        });
    }

    handleConfirmClick = () => {
        this.props.onChange(this.state.selected);

        this.hide();
    }

    handleRelatedConfirmClick = () => {
        this.props.onChange(this.state.selected, true);

        this.hide();
    }

    handleCancelClick = () => {
        this.hide();
    }

    handleRemoveClick = () => {
        this.hide();
        this.props.onRemove();
    }

    show() {
        this.popoutCtrl && this.popoutCtrl.show();
    }

    hide() {
        this.popoutCtrl && this.popoutCtrl.hide();
    }

    componentDidMount() {
        const ctrl = {
            show: event => {
                return this.show(event);
            },
            hide: () => {
                this.hide();
            }
        };

        if (this.props.provideController) {
            if ('current' in this.props.provideController) {
                this.props.provideController.current = ctrl;
            } else {
                this.props.provideController && this.props.provideController(ctrl);
            }
        }
    }

    componentWillUnmount() {
        if (this.props.provideController) {
            if ('current' in this.props.provideController) {
                this.props.provideController.current = null;
            } else {
                this.props.provideController && this.props.provideController(null);
            }
        }
    }

    setPopoutCtrl = ctrl => this.popoutCtrl = ctrl;

    render() {
        const { t } = this.props;

        this.quickOptions = [
            {
                id: 'today',
                text: t('periods.Today'),
                date: moment().endOf('day').toDate()
            },
            {
                id: 'tomorrow',
                text: t('periods.Tomorrow'),
                date: moment().add(1, 'd').endOf('day').toDate()
            },
            {
                id: 'thisWeek',
                text: t('periods.This Week'),
                date: moment().endOf('week').toDate()
            },
            {
                id: 'thisMonth',
                text: t('periods.This Month'),
                date: moment().endOf('month').toDate()
            }
        ];

        return (
            <Popout
                provideController={this.setPopoutCtrl}
                className={'popout-datepicker dark-arrow ' + (this.props.className || '')}
                xAlign={this.props.xAlign}
                defaultXPosition={this.props.defaultXPosition}
                onShow={this.props.onShow}
                onHide={this.props.onHide}
            >
                <div className="popout-inner">
                    <div className="popout-head">
                        {this.props.title ?? t('Select date')}
                    </div>

                    <div className="popout-body">
                        <div className="datepicker-holder">
                            {
                                this.props.showQuickOptions ? (
                                    <div className="datepicker-quicklist">
                                        { this.quickOptions.map(opt => <QuickOption key={opt.id} {...opt} onSelect={this.handleChange} />) }
                                    </div>
                                ) : null
                            }

                            <LocalizedDatePicker
                                selected={this.state.selected}
                                showTimeSelect={this.props.showTimeSelect}
                                minDate={this.props.minDate}
                                onChange={this.handleChange}
                                inline={true}
                                excludeTimes={[
                                    moment().startOf('day').toDate()
                                ]}
                                injectTimes={[
                                    moment().endOf('day').toDate()
                                ]}
                            />
                        </div>
                    </div>

                    <div className="popout-foot">
                        {
                            this.props.onRemove ? <button className="btn btn-small btn-link btn-text-warning" onClick={this.handleRemoveClick}>{this.props.removeText ?? t('common:Clear')}</button> : null
                        }

                        <div className="btn-group btn-group-auto">
                            <button className={'btn btn-small ' + this.props.cancelClassName} onClick={this.handleCancelClick}>{this.props.cancelText ?? t('common:Cancel')}</button>

                            <button className={'btn btn-small ' + this.props.confirmClassName} onClick={this.handleConfirmClick}>{this.props.confirmText ?? t('common:Select')}</button>
                        </div>
                    </div>

                    {
                        (this.props.relatedAfter && this.state.selected < new Date(this.props.relatedAfter)) ||
                        (this.props.relatedBefore && this.state.selected > new Date(this.props.relatedBefore)) ? (
                            <div className="popout-foot popout-foot-secondary">
                                { this.props.relatedCopy }

                                <div className="btn-group btn-group-auto">
                                    <button className={'btn btn-small ' + this.props.confirmClassName} onClick={this.handleRelatedConfirmClick}>{ this.props.relatedConfirmText }</button>
                                </div>
                            </div>
                        ) : null
                    }
                </div>
            </Popout>
        )
    }
}

export default withTranslation(['date', 'common'])(CustomDatePicker);
